import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-6 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex items-center space-x-2" }
const _hoisted_3 = { class: "flex items-center text-xl font-semibold text-gray-800" }
const _hoisted_4 = { class: "mb-4 text-xs font-semibold uppercase tracking-widest text-indigo-200 sm:text-sm" }
const _hoisted_5 = {
  key: 0,
  class: "flex w-full flex-col items-center"
}
const _hoisted_6 = { class: "mb-6 flex justify-center space-x-2 sm:space-x-3" }
const _hoisted_7 = { class: "flex flex-col items-center space-y-3" }
const _hoisted_8 = { class: "text-xs font-light text-indigo-300" }
const _hoisted_9 = {
  key: 1,
  class: "flex flex-col items-center py-6 text-center text-indigo-300"
}
const _hoisted_10 = { class: "text-sm" }
const _hoisted_11 = { class: "space-y-4" }
const _hoisted_12 = { class: "flex items-center text-lg font-bold text-gray-800" }
const _hoisted_13 = { class: "overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm" }
const _hoisted_14 = { class: "min-w-full divide-y divide-gray-200" }
const _hoisted_15 = { class: "bg-gray-50" }
const _hoisted_16 = { class: "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" }
const _hoisted_17 = { class: "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" }
const _hoisted_18 = { class: "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" }
const _hoisted_19 = { class: "divide-y divide-gray-200 bg-white" }
const _hoisted_20 = { class: "whitespace-nowrap px-6 py-4 font-mono text-sm font-bold text-indigo-600" }
const _hoisted_21 = { class: "whitespace-nowrap px-6 py-4 text-sm text-gray-500" }
const _hoisted_22 = { class: "max-w-lg break-words px-6 py-4 text-sm text-gray-600" }
const _hoisted_23 = { key: 0 }
const _hoisted_24 = {
  colspan: "3",
  class: "px-6 py-8 text-center text-sm italic text-gray-500"
}

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ClipboardIcon, CheckIcon, DocumentTextIcon } from '@heroicons/vue/solid';
import { NumberService, WhatsappCodeService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore } from '@/stores';
import { NumberModel, WhatsappCodeModel } from '@/types';
import { applyPhoneMask } from '@/utils/number';
import { moment } from '@/utils/datetime';
import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';

const perPage = 10;
const latestCodeVisibleSeconds = 180;


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsappCodeMonitorPage',
  setup(__props) {

const route = useRoute();
const { t } = useI18n();
const uiStore = useUiStore();

const numberId = parseInt(route.params.id as string, 10);
const number = ref<NumberModel | null>(null);
const codes = ref<WhatsappCodeModel[]>([]);
const latestCode = ref<WhatsappCodeModel | null>(null);

const count = ref(0);
const currentPage = ref(1);
const copied = ref(false);
const newlyReceivedId = ref<number | null>(null);
let pollingInterval: number | null = null;

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const digitChars = computed(() => {
    if (!latestCode.value || !latestCode.value.code) {
        return Array(6).fill('-');
    }
    const codeStr = latestCode.value.code.toString();
    const chars = codeStr.split('');
    while (chars.length < 6) {
        chars.push('-');
    }
    return chars;
});

const formatTime = (dateStr: string): string => {
    return moment(dateStr).format('DD/MM/YYYY HH:mm:ss');
};

const isRecentlyReceived = (code: WhatsappCodeModel): boolean => {
    const receivedAt = moment(code.receivedAt);
    if (!receivedAt.isValid()) return false;

    const secondsSinceReceived = moment().diff(receivedAt, 'seconds');
    return secondsSinceReceived >= 0 && secondsSinceReceived <= latestCodeVisibleSeconds;
};

const setLatestCode = (code?: WhatsappCodeModel) => {
    latestCode.value = code && isRecentlyReceived(code) ? code : null;
};

const copyToClipboard = async () => {
    if (!latestCode.value) return;
    try {
        await navigator.clipboard.writeText(latestCode.value.code);
        copied.value = true;
        toast.success(t('whatsappCode.toast.copiedSuccess'));
        setTimeout(() => {
            copied.value = false;
        }, 1500);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy: ', err);
    }
};

const fetchNumber = async () => {
    try {
        const { data } = await NumberService.get(numberId);
        number.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchCodes = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
        const { data, headers } = await WhatsappCodeService.getCodes(numberId, {
            params: {
                page: currentPage.value,
                per_page: perPage,
            },
        });
        codes.value = data;
        count.value = parseInt(headers['x-pagination-count']) || data.length;

        if (data.length > 0 && currentPage.value === 1) {
            setLatestCode(data[0]);
        } else if (!latestCode.value && data.length > 0) {
            // fallback if we are on page 2+ and didn't load first page yet
            const firstPageRes = await WhatsappCodeService.getCodes(numberId, {
                params: { page: 1, per_page: 1 },
            });
            if (firstPageRes.data.length > 0) {
                setLatestCode(firstPageRes.data[0]);
            }
        } else if (data.length === 0 && currentPage.value === 1) {
            setLatestCode();
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        if (showLoading) setLoading(false);
    }
};

const startPolling = () => {
    pollingInterval = window.setInterval(async () => {
        try {
            // Check for new codes on page 1
            const { data } = await WhatsappCodeService.getCodes(numberId, {
                params: {
                    page: 1,
                    per_page: 1,
                },
            });

            if (data && data.length > 0) {
                const newest = data[0];
                const previousLatestId = latestCode.value?.id || null;
                setLatestCode(newest);
                const isNew = Boolean(latestCode.value && newest.id !== previousLatestId);

                if (isNew) {
                    if (currentPage.value === 1) {
                        // Prepend if not already in current codes list
                        if (!codes.value.some((c) => c.id === newest.id)) {
                            codes.value.unshift(newest);
                            if (codes.value.length > perPage) {
                                codes.value.pop();
                            }
                            // Trigger highlight animation
                            newlyReceivedId.value = newest.id;
                            setTimeout(() => {
                                newlyReceivedId.value = null;
                            }, 2500);
                        }
                    } else {
                        // If user is on page 2+, just refresh current page
                        await fetchCodes(false);
                    }
                }
            } else {
                setLatestCode();
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Polling error:', err);
        }
    }, 5000);
};

const handlePageChange = (page: number) => {
    currentPage.value = page;
    fetchCodes(true);
};

onMounted(async () => {
    await fetchNumber();
    await fetchCodes(true);
    startPolling();
});

onUnmounted(() => {
    if (pollingInterval) {
        window.clearInterval(pollingInterval);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { "fallback-to": { name: 'ListWhatsappCodeNumbers' } }),
          _createElementVNode("h1", _hoisted_3, [
            _createVNode(SVGWhatsApp, { class: "mr-1.5 h-6 w-6 text-[#25D366]" }),
            _createTextVNode(" " + _toDisplayString(number.value
                                ? _ctx.$t('whatsappCode.monitoringNumber', {
                                      number: _unref(applyPhoneMask)(number.value.number),
                                  })
                                : _ctx.$t('whatsappCode.title')), 1)
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, { class: "mx-auto flex max-w-xl flex-col items-center overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-900 to-indigo-950 p-6 text-white shadow-lg sm:p-8" }, {
      default: _withCtx(() => [
        _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t('whatsappCode.lastReceivedCode')), 1),
        (latestCode.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(digitChars.value, (char, idx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `digit-${idx}`,
                    class: "flex h-14 w-11 transform items-center justify-center rounded-xl border border-white/20 bg-white/10 font-mono text-2xl font-extrabold text-white shadow-inner backdrop-blur-md transition-transform duration-150 hover:scale-105 sm:h-20 sm:w-14 sm:text-4xl"
                  }, _toDisplayString(char), 1))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("button", {
                  class: _normalizeClass(["flex items-center space-x-2 rounded-lg px-6 py-2 text-sm font-medium shadow-md transition-all duration-150 focus:outline-none", [
                            copied.value
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700',
                        ]]),
                  onClick: copyToClipboard
                }, [
                  (!copied.value)
                    ? (_openBlock(), _createBlock(_unref(ClipboardIcon), {
                        key: 0,
                        class: "mr-1 h-4 w-4"
                      }))
                    : (_openBlock(), _createBlock(_unref(CheckIcon), {
                        key: 1,
                        class: "mr-1 h-4 w-4 text-green-300"
                      })),
                  _createElementVNode("span", null, _toDisplayString(copied.value ? _ctx.$t('whatsappCode.copied') : _ctx.$t('whatsappCode.copyCode')), 1)
                ], 2),
                _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t('whatsappCode.table.receivedAt')) + ": " + _toDisplayString(formatTime(latestCode.value.receivedAt)), 1)
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createVNode(SVGWhatsApp, { class: "mb-3 h-10 w-10 animate-pulse text-[#25D366]" }),
              _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t('whatsappCode.noCodesReceived')), 1)
            ]))
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("h2", _hoisted_12, [
        _createVNode(_unref(DocumentTextIcon), { class: "mr-1 h-5 w-5 text-gray-500" }),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('whatsappCode.history')), 1)
      ]),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("table", _hoisted_14, [
          _createElementVNode("thead", _hoisted_15, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", _hoisted_16, _toDisplayString(_ctx.$t('whatsappCode.table.code')), 1),
              _createElementVNode("th", _hoisted_17, _toDisplayString(_ctx.$t('whatsappCode.table.receivedAt')), 1),
              _createElementVNode("th", _hoisted_18, _toDisplayString(_ctx.$t('whatsappCode.table.transcript')), 1)
            ])
          ]),
          _createElementVNode("tbody", _hoisted_19, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(codes.value, (code) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: code.id,
                class: _normalizeClass([[
                                newlyReceivedId.value === code.id
                                    ? 'animate-new-row'
                                    : 'hover:bg-gray-50/50',
                            ], "transition-colors duration-150"])
              }, [
                _createElementVNode("td", _hoisted_20, _toDisplayString(code.code), 1),
                _createElementVNode("td", _hoisted_21, _toDisplayString(formatTime(code.receivedAt)), 1),
                _createElementVNode("td", _hoisted_22, _toDisplayString(code.transcript || '-'), 1)
              ], 2))
            }), 128)),
            (codes.value.length === 0)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_23, [
                  _createElementVNode("td", _hoisted_24, _toDisplayString(_ctx.$t('core.messages.noRecordsFound')), 1)
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      (codes.value.length)
        ? (_openBlock(), _createBlock(_component_UiPagination, {
            key: 0,
            modelValue: currentPage.value,
            "onUpdate:modelValue": [
              _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
              handlePageChange
            ],
            count: count.value,
            perPage: perPage
          }, null, 8, ["modelValue", "count"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})